import { Spin, Table, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import editIcon from "../../../../../Assets/svg/table-action-edit.svg";
import previewIcon from "../../../../../Assets/svg/table-action-preview.svg";
import Filters from "./Filters";
import Preview from "../../../planning/VisitBoard/TableView/modals/Preview";
import EditSiteVisitCardModal from "../../../planning/VisitBoard/Modals/EditSiteVisitCardModal";
import {
  formatDate,
  formatDateAndTime,
} from "../../../../../services/helpFunctions/formatDateAndTime";
import FacilityTag from "../../../../components/FacilityTag/FacilityTag";
import { REPORTING_DEFAULT_COLUMNS } from "../../../../components/SiteVisitColumns/siteVisitColumnOrder";
import { useSiteVisitColumns } from "../../../../components/SiteVisitColumns/useSiteVisitColumns";

function TableViewReporting({
  data,
  handleFilterChange,
  loading,
  filters,
  setFilters,
  setDataForExcel,
  onPageChange,
  pagination,
  setPagination,
  onRowSelectionChange,
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 576);
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 576);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const handleTogglePreviewModal = (record) => {
    const siteVisitCardId = record;
    setSelectedSiteVisit(siteVisitCardId);
    setShowPreviewModal(!showPreviewModal);
  };

  const handleToggleEditModal = (record) => {
    const siteVisitCardId = record;
    setSelectedSiteVisit(siteVisitCardId);
    setShowEditModal(!showEditModal);
  };

  const statusMappingFieldTeamReportingStatus = (status) => {
    switch (status) {
      case 1:
        return {
          text: "Data collection",
          className: "dataCollection",
          fill: "#3B81FE",
        };
      case 2:
        return {
          text: "Data check",
          className: "dataCheck",
          fill: "#045B46",
        };
      case 3:
        return {
          text: "Field Reporting writing",
          className: "fieldReportWriting",
          fill: "var(--Range-assessment-Excellent, #3D9B4C)",
        };
      case 4:
        return {
          text: "Translation",
          className: "translation",
          fill: "#918d27",
        };
      case 5:
        return {
          text: "Field Reporting Completed",
          className: "fieldReportCompleted",
          fill: "#9670b8",
        };
      case 6:
        return {
          text: "Field report approved",
          className: "fieldReportApproved",
          fill: "var(--Graph-Green-1, #06768E)",
        };
      case 7:
        return {
          text: "Field report submitted to client",
          className: "fieldReportSubmitted",
          fill: "var(--Graph-Green-3, #6FC5D5)",
        };
      default:
        return {
          text: "Unknown",
          className: "noStatus",
          fill: "rgba(172, 12, 12, 0.777)",
        };
    }
  };

  const statusMappingSiteVisitStatus = (status) => {
    // Ensure status is a number
    const numericStatus = Number(status);
    switch (numericStatus) {
      case 1:
        return {
          text: "No status",
          className: "noStatus",
          fill: "rgba(172, 12, 12, 0.777)",
        };
      case 2:
        return {
          text: "Data collection in progress",
          className: "progress",
          fill: "#3B81FE",
        };
      case 3:
        return {
          text: "Selected for monitoring",
          className: "monitoring",
          fill: "#FC9403",
        };
      case 4:
        return { text: "Review", className: "review", fill: "#9670B8" };
      case 5:
        return { text: "Delivered", className: "delivered", fill: "#045B46" };
      case 6:
        return { text: "Debrief", className: "debrief", fill: "#0F7507" };
      default:
        return {
          text: "No status",
          className: "noStatus",
          fill: "rgba(172, 12, 12, 0.777)",
        };
    }
  };

  function getMonth(monthNumber) {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const monthIndex = parseInt(monthNumber, 10) - 1;

    return months[monthIndex] || "";
  }

  const columns = [
    {
      title: <span className="styledTitle">Site Visit Card Identifier</span>,
      dataIndex: "identifier",
      key: "identifier",
      ellipsis: true,
      width: 250,
      sorter: (a, b) => (a.identifier || "").localeCompare(b.identifier || ""),
      render: (record) => <span className="styledDataIndex3">{record}</span>,
    },
    {
      title: <span className="styledTitle">Site Name</span>,
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      width: 250,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (record) => (
        <span className="styledDataIndex3">{record || "N/A"}</span>
      ),
    },
    {
      title: <span className="styledTitle">Site Visit Status</span>,
      dataIndex: "tracking.siteVisitStatus",
      key: "siteVisitStatus",
      width: 250,
      sorter: (a, b) => {
        const statusA = a?.siteVisitStatus || "";
        const statusB = b?.siteVisitStatus || "";
        return String(statusA).localeCompare(String(statusB));
      },
      render: (text, record) => {
        const status = record?.siteVisitStatus;
        const {
          text: statusText,
          className,
          fill,
        } = statusMappingSiteVisitStatus(status);
        return (
          <div className={className}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8px"
              height="8px"
              viewBox="0 0 9 9"
            >
              <circle cx="4.5" cy="4.27344" r="4" fill={fill} />
            </svg>
            <span>{statusText}</span>
          </div>
        );
      },
    },
    {
      title: <span className="styledTitle">Facility</span>,
      dataIndex: "facility",
      key: "facility",
      ellipsis: true,
      width: 250,
      sorter: (a, b) =>
        (a.facility?.name || "").localeCompare(b.facility?.name || ""),
      render: (facility) => <FacilityTag facility={facility} />,
    },
    {
      title: <span className="styledTitle">Location</span>,
      sorter: (a, b) =>
        a.locationDetails?.country.localeCompare(b.locationDetails?.country),
      key: "location",
      dataIndex: "locationDetails.country",
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.locationDetails?.country}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Governate/State</span>,
      sorter: (a, b) => a.siteVisitName.localeCompare(b.siteVisitName),
      dataIndex: "locationDetails.state",
      key: "state",
      ellipsis: true,
      width: 250,
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record?.locationDetails?.state || "This country has no states."}
        </span>
      ),
    },
    {
      title: <span className="styledTitle">Field Report Status</span>,
      dataIndex: "tracking.fieldTeamReportingStatus",
      key: "fieldTeamReportingStatus",
      width: 250,
      sorter: (a, b) => {
        const statusA = a.tracking?.fieldTeamReportingStatus || "";
        const statusB = b.tracking?.fieldTeamReportingStatus || "";
        return String(statusA).localeCompare(String(statusB));
      },
      render: (value, record) => {
        const { text, className, fill } = statusMappingFieldTeamReportingStatus(
          record?.tracking?.fieldTeamReportingStatus
        );
        return (
          <div className={className}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8px"
              height="8px"
              viewBox="0 0 9 9"
            >
              <circle cx="4.5" cy="4.27344" r="4" fill={fill} />
            </svg>
            <span className="styledDataIndex3">{text}</span>
          </div>
        );
      },
    },

    {
      title: <span className="styledTitle">IP</span>,
      dataIndex: "ipPlan.implementingPartner.name",
      key: "implementingPartner",
      sorter: (a, b) =>
        (a.ipPlan?.implementingPartner?.name || "").localeCompare(
          b.ipPlan?.implementingPartner?.name || ""
        ),
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.ipPlan?.implementingPartner?.name}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },

    {
      title: <span className="styledTitle">Cluster</span>,
      dataIndex: "cluster.name",
      key: "cluster",
      sorter: (a, b) =>
        (a.cluster?.name || "").localeCompare(b.cluster?.name || ""),
      render: (text, record) => (
        <span className="styledDataIndex3">{record.cluster?.name}</span>
      ),
      ellipsis: true,
      width: 250,
    },

    {
      title: <span className="styledTitle">TPM Coordinator</span>,
      dataIndex: `logistic.tpmCoordinator`,
      key: "tpmCoordinator",
      sorter: (a, b) =>
        (a?.logistic?.tpmCoordinator?.firstName || "").localeCompare(
          b?.logistic?.tpmCoordinator?.firstName || ""
        ),
      render: (text, record) => {
        const { firstName, lastName, email } =
          record?.logistic?.tpmCoordinator || {};

        if (firstName && lastName) {
          return (
            <span className="styledDataIndex3">
              {firstName} {lastName}
            </span>
          );
        } else if (email) {
          return <span className="styledDataIndex3">{email}</span>;
        }
        return <span className="styledDataIndex3">N/A</span>;
      },
      ellipsis: true,
      width: 250,
    },

    {
      // Keyed "name" until now — the same key the Site Name column uses, which
      // gave antd two columns with one identity.
      title: <span className="styledTitle">Team</span>,
      dataIndex: "logistic.team.name",
      key: "team",
      sorter: (a, b) =>
        (a?.logistic?.team?.name || "").localeCompare(
          b?.logistic?.team?.name || ""
        ),
      render: (text, record) => (
        <span className="styledDataIndex3">{record?.logistic?.team?.name}</span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Reporting Month-Year</span>,
      dataIndex: "tracking.reportingMonthYear",
      key: "reportingMonthYear",
      sorter: (a, b) =>
        (
          a.tracking?.reportingYear + "-" + a.tracking?.reportingMonth || ""
        ).localeCompare(
          b.tracking?.reportingYear + "-" + b.tracking?.reportingMonth || ""
        ),
      render: (text, record) => (
        <span className="styledDataIndex3">
          {`${getMonth(record.tracking?.reportingMonth)}, 
          ${record.tracking?.reportingYear}`}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Actual Date of Visit</span>,
      dataIndex: "tracking.siteTrackingVisitDates",
      key: "actualDateOfVisit",
      sorter: (a, b) => {
        const aDate = a.tracking?.siteTrackingVisitDates?.[0]?.visitDate || "";
        const bDate = b.tracking?.siteTrackingVisitDates?.[0]?.visitDate || "";
        return aDate.localeCompare(bDate);
      },
      render: (data, record) =>
        record.tracking?.siteTrackingVisitDates?.length ? (
          record.tracking.siteTrackingVisitDates.map((date) => (
            <span
              className="styledDataIndex3"
              style={{ display: "block" }}
              key={date.id}
            >
              {formatDate(date?.visitDate)}
            </span>
          ))
        ) : (
          <span className="styledDataIndex3">
            No actual date of visit available
          </span>
        ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Training completed</span>,
      dataIndex: "tracking.trainingIsCompleted",
      key: "trainingIsCompleted",
      sorter: (a, b) => {
        const trainingCompletedA = a.tracking?.trainingIsCompleted
          ? "Yes"
          : "No";
        const trainingCompletedB = b.tracking?.trainingIsCompleted
          ? "Yes"
          : "No";
        return trainingCompletedA.localeCompare(trainingCompletedB);
      },
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.tracking?.trainingIsCompleted ? "Yes" : "No"}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Postponed</span>,
      dataIndex: ["logistic", "isPostponed"],
      key: "isPostponed",
      sorter: (a, b) => {
        const aValue = a.logistic?.isPostponed || "";
        const bValue = b.logistic?.isPostponed || "";
        return aValue.toString().localeCompare(bValue.toString());
      },
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.logistic?.isPostponed ? "Yes" : "No"}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    // Reporting-phase columns. This board is where the field report is chased,
    // so the two dates that close it out belong here rather than only in the
    // reporting list's edit modal.
    {
      title: <span className="styledTitle">% Data Collection Completed</span>,
      key: "percentageCompleted",
      sorter: (a, b) =>
        (a.tracking?.percentageOfDataCollectionCompleted || 0) -
        (b.tracking?.percentageOfDataCollectionCompleted || 0),
      render: (text, record) => {
        const value = record.tracking?.percentageOfDataCollectionCompleted;
        return (
          <span className="styledDataIndex3">
            {value === null || value === undefined ? "—" : `${value}%`}
          </span>
        );
      },
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Date of Report</span>,
      key: "dateOfReport",
      sorter: (a, b) =>
        (a.tracking?.dateOfReport || "").localeCompare(
          b.tracking?.dateOfReport || ""
        ),
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.tracking?.dateOfReport
            ? formatDate(record.tracking.dateOfReport)
            : "—"}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      title: <span className="styledTitle">Date of Debrief</span>,
      key: "dateOfDebrief",
      sorter: (a, b) =>
        (a.tracking?.dateOfDebrief || "").localeCompare(
          b.tracking?.dateOfDebrief || ""
        ),
      render: (text, record) => (
        <span className="styledDataIndex3">
          {record.tracking?.dateOfDebrief
            ? formatDate(record.tracking.dateOfDebrief)
            : "—"}
        </span>
      ),
      ellipsis: true,
      width: 250,
    },
    {
      key: "actions",
      fixed: isLargeScreen && data.length > 0 ? "right" : undefined,
      width: 120,
      render: (text, record) => (
        <div className="actionDiv">
          <Tooltip title="Preview site visit">
            <img
              src={previewIcon}
              style={{ cursor: "pointer" }}
              alt="previewicon"
              onClick={() => handleTogglePreviewModal(record.id)}
            />
          </Tooltip>
          {/* <img
            src={editIcon}
            style={{  cursor: "pointer" }}
            alt="editicon"
            onClick={() => handleToggleEditModal(record.id)}
          /> */}
        </div>
      ),
    },
  ];

  const {
    columns: visibleColumns,
    columnsBar,
    components: tableComponents,
  } = useSiteVisitColumns({
    tableId: "fieldTeamStatusBoard",
    columns,
    defaultKeys: REPORTING_DEFAULT_COLUMNS,
  });

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      onRowSelectionChange(selectedRowKeys);
    },
  };

  return (
    <>
      <Filters
        filters={filters}
        setFilters={setFilters}
        setPagination={setPagination}
      />
      <div
        className="w-100 my-3 d-flex justify-content-start gap-2"
        style={{
          color: "var(--2, #AA1A5F)",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: "600",
          lineHeight: "24px",
        }}
      >
        {loading ? (
          <Spin className="me-2" size="small" spinning={loading} />
        ) : (
          `Total: ${pagination.total} visits`
        )}
        {columnsBar && <span className="ms-auto">{columnsBar}</span>}
      </div>
      <div>
        <Table
          components={tableComponents}
          rowKey="id"
          rowSelection={data.length > 0 ? rowSelection : ""}
          columns={data?.length > 0 ? visibleColumns : []}
          dataSource={data}
          loading={loading}
          className="tableTPM"
          scroll={{ x: "max-content" }}
          pagination={{
            ...pagination,
            onChange: onPageChange,
          }}
        />
      </div>
      <Preview
        visible={showPreviewModal}
        onClose={handleTogglePreviewModal}
        siteVisitCardId={selectedSiteVisit}
      />
      <EditSiteVisitCardModal
        visible={showEditModal}
        onCancel={handleToggleEditModal}
        siteVisitCardId={selectedSiteVisit}
      />
    </>
  );
}

export default TableViewReporting;
